/** @format */

import React from "react";
import { Alert } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import { useLocale } from "../../i18n/hooks";
import i18n from "../../i18n/init";
import stringsCy from "./locales/strings.cy.json";

type Props = {
  statusCode?: number;
  statusMessage?: string;
  hiddenMessage?: string;
  actions: ("report" | "newResource" | "reload" | "subject")[];
};

/** TYPICAL USAGE WITH RTK QUERY ERRORS, where `error` is the error object returned by RTK
 * ``` TSX
  <ErrorPage
    statusCode={
      "status" in error &&
      typeof error.status === "number"
        ? error.status
        : undefined
    }
    hiddenMessage={
      "data" in error && typeof error.data === "string"
        ? error.data
        : undefined
    }
    actions={["report", "newResource"]}
  />
* ```
*/

/**
 * This component uses either a relevant status code or message to display a formatted error to the user
 * @param statusCode default messages for 400, 401, 403, 404, and 500. See https://developer.mozilla.org/en-US/docs/Web/HTTP/Status
 *  NOTE: these messages do not translate well and should only be used without `statusMessage` in place for an unpredicted error code
 *
 * @param statusMessage overrides default message from status code
 *
 * @param hiddenMessage the message to be reported with the report action
 *
 * @param actions "report": a mailto button to report the error.
 *  "newResource": a link to the RF site.
 *  "reload": a reload page button (also shows when the status and message are undefined)
 *  "subject": using the location params, link user back to current subject. With no param, link to all resources
 *  TODO: add a login action when sure what the login url will be
 */
const ErrorPage: React.FC<Props> = ({
  statusCode,
  statusMessage,
  hiddenMessage,
  actions,
}) => {
  const { subject } = useParams();
  const location = useLocation();
  const { t: _ } = useTranslation();
  const locale = useLocale();
  i18n.addResources("cy", "translation", stringsCy);

  return (
    <Alert
      variant="danger"
      className="position-absolute top-50 start-50 translate-middle p-4"
      data-testid="error"
    >
      {statusMessage ? (
        <h4 data-testid="statusMsg">{statusMessage}</h4>
      ) : statusCode === 400 ? (
        <h4 data-testid="defaultMsg">{_("Bad request")}</h4>
      ) : statusCode === 401 ? (
        <h4>{_("Please sign in to view this")}</h4>
      ) : statusCode === 403 ? (
        <h4>{_("You do not have access to this content")}</h4>
      ) : statusCode === 404 ? (
        <h4>{_("Not found")}</h4>
      ) : (
        statusCode === 500 && <h4>{_("Server error")}</h4>
      )}

      {statusCode ? (
        <h5>
          {_("Error")}: {statusCode}
        </h5>
      ) : (
        <h5>
          {_(
            "Unknown error, please attempt to reload the page before reporting it.",
          )}
        </h5>
      )}

      <hr />

      <p>
        {statusCode === undefined ||
          (actions.includes("reload") && (
            <a
              className="text-decoration-none"
              href={window.location.toString()}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                className="bi bi-arrow-clockwise"
                viewBox="0 0 16 16"
              >
                <path
                  fillRule="evenodd"
                  d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2z"
                />
                <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466" />
              </svg>{" "}
              {_("Reload page")}
              <br />
            </a>
          ))}
        {actions.includes("report") && (
          <a
            className="text-decoration-none"
            // NOTE: %0D%0A is mailto new line
            // NOTE: this message is untranslated.
            href={
              `mailto:${_("resources@wjec.co.uk")}` +
              `?body=Error Location: ${window.location.href}` +
              ` ${!!statusCode ? "%0D%0AStatus Code: " + statusCode : ""}` +
              ` ${
                statusMessage ? "%0D%0AError Message: " + statusMessage : ""
              }` +
              ` ${
                hiddenMessage
                  ? "%0D%0AServer Error Message: " + hiddenMessage
                  : ""
              }`
            }
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              className="bi bi-flag"
              viewBox="0 0 16 16"
            >
              <path d="M14.778.085A.5.5 0 0 1 15 .5V8a.5.5 0 0 1-.314.464L14.5 8l.186.464-.003.001-.006.003-.023.009a12 12 0 0 1-.397.15c-.264.095-.631.223-1.047.35-.816.252-1.879.523-2.71.523-.847 0-1.548-.28-2.158-.525l-.028-.01C7.68 8.71 7.14 8.5 6.5 8.5c-.7 0-1.638.23-2.437.477A20 20 0 0 0 3 9.342V15.5a.5.5 0 0 1-1 0V.5a.5.5 0 0 1 1 0v.282c.226-.079.496-.17.79-.26C4.606.272 5.67 0 6.5 0c.84 0 1.524.277 2.121.519l.043.018C9.286.788 9.828 1 10.5 1c.7 0 1.638-.23 2.437-.477a20 20 0 0 0 1.349-.476l.019-.007.004-.002h.001M14 1.221c-.22.078-.48.167-.766.255-.81.252-1.872.523-2.734.523-.886 0-1.592-.286-2.203-.534l-.008-.003C7.662 1.21 7.139 1 6.5 1c-.669 0-1.606.229-2.415.478A21 21 0 0 0 3 1.845v6.433c.22-.078.48-.167.766-.255C4.576 7.77 5.638 7.5 6.5 7.5c.847 0 1.548.28 2.158.525l.028.01C9.32 8.29 9.86 8.5 10.5 8.5c.668 0 1.606-.229 2.415-.478A21 21 0 0 0 14 7.655V1.222z" />
            </svg>{" "}
            {_("Report")}
            <br />
          </a>
        )}

        {actions.includes("newResource") && (
          <a
            className="text-decoration-none"
            href={"https://filter.resources.wjec.co.uk/" + locale}
            data-testid="newResource"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              className="bi bi-search"
              viewBox="0 0 16 16"
            >
              <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0" />
            </svg>{" "}
            {_("Find a new resource")}
            <br />
          </a>
        )}

        {actions.includes("subject") && (
          <a
            className="text-decoration-none"
            href={`${location.pathname.slice(
              0,
              location.pathname.indexOf("/"),
            )}/${locale}/${subject}`}
            data-testid="newResource"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              className="bi bi-search"
              viewBox="0 0 16 16"
            >
              <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0" />
            </svg>{" "}
            {_("Find a new resource")}
            <br />
          </a>
        )}
      </p>
    </Alert>
  );
};

export default ErrorPage;
